import React, { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getCookie } from '@/lib/http/bffClient';
import { useJobProgressStream } from '@/features/system/jobs/hooks/useJobProgressStream';
import JobProgressViewer from '@/features/system/jobs/components/JobProgressViewer';
import { sanitizeFile, DEFAULT_MAX_BYTES } from '../lib/fileSanitizer';

const UploadMp3Panel: React.FC = () => {
  const [dragOver, setDragOver] = useState(false);
  const [status, setStatus] = useState<'idle'|'init'|'uploading'|'completing'|'done'|'error'>('idle');
  const [progress, setProgress] = useState<number>(0);
  const [message, setMessage] = useState<string>('');
  const [runId, setRunId] = useState<string | number | null>(null);
  const [lastCompleted, setLastCompleted] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const pickFile = () => inputRef.current?.click();

  const reset = () => {
    setStatus('idle');
    setProgress(0);
    setMessage('');
    setRunId(null);
    setErrors([]);
    setLastCompleted(false);
    try { if (inputRef.current) inputRef.current.value = ''; } catch {}
  };

  const proxyUpload = useCallback(async (file: File) => {
  setStatus('uploading');
  setLastCompleted(false);
    setMessage('Uploading via server…');
    const url = new URL('/bff/services/media/upload/mp3/proxy', window.location.origin);
    url.searchParams.set('filename', file.name);
    const csrf = getCookie('csrf');
    const res = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        'Content-Type': file.type || 'audio/mpeg',
        ...(csrf ? { 'x-csrf': csrf } : {}),
        'accept': 'application/json',
      },
      body: file,
      credentials: 'include',
    });
    if (!res.ok) {
      const txt = (await res.text()).slice(0, 200);
      throw new Error(`${res.status} ${res.statusText} ${txt}`);
    }
    const out = await res.json();
    if (!out?.ok) throw new Error('Proxy upload failed');
    return out as { ok: true; status: string; runId?: number|string; jobId?: number|string; eventsUrl?: string; latestUrl?: string; published?: boolean };
  }, []);

  const handleUploadFlow = useCallback(async (file: File) => {
    if (!file) return;
    if (!/^audio\/(mpeg|mp3)$/i.test(file.type) && !file.name.toLowerCase().endsWith('.mp3')) {
      setStatus('error');
      setMessage('Please select an MP3 file');
      return;
    }
    try {
      const out = await proxyUpload(file);
      const rId = (out.runId ?? out.jobId) as any;
      if (rId) setRunId(rId);
  setStatus('done');
  // No persistent message; progress viewer will show stages.
    } catch (e: any) {
      setStatus('error');
      setMessage(`Proxy upload failed: ${e?.message || String(e)}`);
    }
  }, [proxyUpload]);

  // File selection + DnD handlers
  const onFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    // Enforce 5 MB limit and MP3 type
    const res = sanitizeFile(file, {
      maxBytes: DEFAULT_MAX_BYTES,
      acceptMime: ['audio/mpeg', 'audio/mp3'],
      acceptExt: ['mp3'],
    });
    if (!res.ok) {
      setErrors([res.message]);
      setStatus('error');
      setMessage(res.message);
      return;
    }
    setErrors([]);
    void handleUploadFlow(file);
  };
  const onDrop: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragOver(false); onFiles(e.dataTransfer?.files || null);
  };
  const onDragOver: React.DragEventHandler<HTMLDivElement> = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(true); };
  const onDragLeave: React.DragEventHandler<HTMLDivElement> = (e) => { e.preventDefault(); e.stopPropagation(); setDragOver(false); };

  const showUploadFrame = status === 'idle';
  const showProgressArea = !showUploadFrame; // uploading, done (processing), error

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-sm font-semibold text-neutral-300">Upload MP3</h3>

      {showUploadFrame && (
        <div
          className={`${dragOver ? 'border-emerald-500 bg-emerald-500/5' : 'border-dashed border-neutral-700/60'} rounded-md border-2 px-6 py-10 text-center cursor-pointer select-none transition-colors`}
          onClick={pickFile}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          role="button"
          aria-label="Upload MP3 drag and drop"
        >
          <div className="text-xs text-neutral-400">
            {lastCompleted ? (
              <>
                Job finished successfully. <button
                  onClick={pickFile}
                  className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-600 hover:bg-emerald-500 text-white text-[11px]"
                >Upload another MP3</button>
              </>
            ) : (
              <>Drag & drop an MP3 or <span className="text-emerald-400 underline decoration-emerald-500/30">browse your song</span></>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="audio/mpeg,audio/mp3,.mp3"
            className="hidden"
            onChange={(e) => onFiles(e.target.files)}
          />
        </div>
      )}

      {showProgressArea && (
        <div className="rounded-md border border-neutral-700/60 bg-neutral-900/40 p-4 space-y-3">
          <div className="text-[12px] text-neutral-300 leading-relaxed">
            <div className="mb-2 font-medium text-neutral-200">{status === 'uploading' ? 'Uploading…' : (status === 'done' ? 'Processing Started' : status === 'error' ? 'Upload Error' : 'Status')}</div>
            {(status === 'uploading' || status === 'error') && message && (
              <div className="mb-2 text-neutral-400 whitespace-pre-line">{message}</div>
            )}
            {status === 'error' && (
              <div className="mt-2 flex items-center gap-2">
                <button
                  className="px-2 py-1 rounded bg-emerald-600/90 hover:bg-emerald-600 text-black text-[11px]"
                  onClick={() => { reset(); pickFile(); }}
                >Retry (pick another file)</button>
                <button
                  className="px-2 py-1 rounded border border-neutral-700 text-neutral-300 hover:text-white text-[11px]"
                  onClick={() => reset()}
                >Dismiss</button>
              </div>
            )}
            {status === 'uploading' && (
              <div className="w-full h-2 bg-neutral-800 rounded overflow-hidden">
                <div className="h-2 bg-emerald-500 transition-all duration-300" style={{ width: `${progress}%` }} />
              </div>
            )}
            {runId && (
              <div className="mt-3">
                <Mp3JobProgress runId={runId} onComplete={() => {
                  // Auto-return to idle state after processing fully completes
                  setLastCompleted(true);
                  setRunId(null);
                  setStatus('idle');
                  setMessage('');
                }} onRateLimitExpire={() => {
                  // When server rate limit countdown ends, return to default upload state
                  reset();
                }} />
              </div>
            )}
          </div>
          {/* No manual reset button; auto reset happens after completion */}
        </div>
      )}
      {/* Minimal inline errors */}
      {errors.length > 0 && (
        <ul className="mt-2 text-[11px] text-rose-300 space-y-1">
          {errors.map((msg, i) => (
            <li key={i} className="truncate" title={msg}>• {msg}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

function Mp3JobProgress({ runId, onComplete, onRateLimitExpire }: { runId: string | number; onComplete?: () => void; onRateLimitExpire?: () => void }) {
  const qc = useQueryClient();
  const progress = useJobProgressStream(runId, { useSSE: true, fallbackPollMs: 4000 });
  const stage = progress.latest?.stage?.toLowerCase();
  const isFinal = (progress.latest as any)?.stack_final === true;
  const done = isFinal || stage === 'job_completed' || stage === 'done' || stage === 'completed' || stage === 'success' || progress.percent === 100;
  if (done && onComplete) {
    // Defer to next microtask so parent state updates after this render
    queueMicrotask(() => {
      try { qc.invalidateQueries({ queryKey: ['audioLibrary'] }); } catch {}
      onComplete();
    });
  }
  // If rate-limited countdown hits 0 (or is missing), take user back to default Upload MP3 state.
  // We mirror the viewer's detection to avoid prop drilling internal countdown.
  React.useEffect(() => {
    const cur: any = progress.latest || {};
    const st = String(cur.stage || '').toLowerCase();
    const err = String(cur.error || '').toLowerCase();
    const msg = String(cur.message || '').toLowerCase();
    const isRateLimited = /rate[ _-]?limited/.test(st) || /rate[ _-]?limited/.test(err) || /rate[ _-]?limited/.test(msg);
    if (!isRateLimited) return;
    const v = cur.wait_seconds ?? cur.retry_in_seconds ?? cur.backoff_seconds ?? cur.retry_after ?? cur.wait_remain_sec ?? null;
    const ws = (typeof v === 'number') ? v : (typeof v === 'string' ? Number(v) : null);
    // Immediate return to default state if already 0 or invalid
    if (ws == null || !isFinite(ws) || ws <= 0) {
      onRateLimitExpire?.();
      return;
    }
    const t = setTimeout(() => {
      onRateLimitExpire?.();
    }, Math.max(0, ws * 1000));
    return () => clearTimeout(t);
  }, [progress.latest, onRateLimitExpire]);
  return (
    <JobProgressViewer
      events={progress.events}
      latest={progress.latest}
      loading={progress.loading}
      error={progress.error}
      percent={progress.percent}
    />
  );
}

export default UploadMp3Panel;
